import React, { Component } from 'react';
import { withRouter } from 'next/router';
import PropTypes from 'prop-types';
import { compose, graphql, withApollo } from 'react-apollo';

import { addTiming, types } from '../../lib/timings';
import { buildAmendmentAvailabilityPath, hasUpdatedQuoteFromBooking } from './helpers';
import getAvailabilityOptions from '../../lib/availability';
import getFetchPolicyWithExpiry from '../../lib/getFetchPolicyWithExpiry';

import { dictionaryItem } from '../../hocs/withDictionary';
import GET_AVAILABILITY from './graphql/getAvailability.gql';
import GET_BOOKING_AMENDMENT_AVAILABILITY from './graphql/getBookingAmendmentAvailabilitySearch.gql';
import GET_CONFIGURATION from '../../config/graphql/getConfiguration';
import IbePropTypes from '../../IbePropTypes';
import FetchPolicy from '../../constants/FetchPolicy';

import * as _Types from '../../lib/jsdocTypedefs';
import { ABORT_ERROR_MESSAGE } from '../../lib/customFetch';

export const AvailabilityContext = React.createContext({});

const defaultData = { availability: { pitchTypes: [] } };
class AvailabilitySearchQuery extends Component {
  static propTypes = {
    booking: PropTypes.shape(IbePropTypes.booking),
    client: PropTypes.shape(IbePropTypes.client).isRequired,
    children: PropTypes.func.isRequired,
    isAmend: PropTypes.bool.isRequired,
    onCompleted: PropTypes.func.isRequired,
    router: PropTypes.shape(IbePropTypes.router).isRequired,
    variables: PropTypes.shape({
      calendar: PropTypes.shape({
        start: PropTypes.string,
        end: PropTypes.string,
      }),
      payload: PropTypes.shape({
        start: PropTypes.string,
        end: PropTypes.string,
      }),
      partyMembers: PropTypes.arrayOf(PropTypes.shape(IbePropTypes.partyMember)),
      siteCode: PropTypes.string,
      productCode: PropTypes.number,
      placeType: PropTypes.number,
      pitchId: PropTypes.string,
      pitchTypeId: PropTypes.string,
    }).isRequired,
    configuration: PropTypes.shape(IbePropTypes.configuration).isRequired,
  }

  static defaultProps = {
    booking: {},
  }

  constructor(props) {
    super(props);

    this.state = {
      availabilitySearch: {
        error: null,
        loading: false,
        data: defaultData,
      },
      availabilityTotal: {
        error: null,
        loading: false,
        data: defaultData,
      },
      inputDateError: null,
    };

    this.availabilitySearchObservable = null;
  }

  handleError = type => (error) => {
    this.setState({
      [type]: {
        error,
        data: defaultData,
      },
    });
  }

  /**
 * Handles whole-month (42-day 6x7) availability. This response
 * is used to fill the availability calendar and doesn't take
 * into account restrictions, it is purely for the presentation
 * of prices on the calendar
 * Can be identified by the param costPerNight=true
 * @returns {_Types.AvailabilitySearchResponse}
 */
  fetchAvailabilitySearch = async () => {
    const {
      client,
      variables: {
        calendar: {
          start,
        },
        partyMembers,
        siteCode,
        productCode,
        placeType,
      },
      configuration,
    } = this.props;

    const product = configuration?.products?.find?.((p) => p?.productCode === productCode) ?? {};

    const availabilityOptionsVariables = {
      variables: {
        costPerNight: true,
        partyMembers,
        productCode,
        siteCode,
        start,
        placeType,
      },
    };

    const { variables } = getAvailabilityOptions(availabilityOptionsVariables, product);

    // the async query below can return response out of order of calling
    // we use a uuid to ensure only the last called utilized.
    const cacheKey = `GET_AVAILABILITY${JSON.stringify(availabilityOptionsVariables.variables)}`;

    this.setState({
      availabilitySearchLoading: true,
    });

    try {
      // We use a small amount of cache here to make the UX of
      // clicking back and forth in the calender a little better
      const response = await client.query({
        query: GET_AVAILABILITY,
        fetchPolicy: getFetchPolicyWithExpiry(cacheKey, {
          defaultPolicy: FetchPolicy.CACHE_FIRST,
          expiry: 1000 * 60 * 3, // 3 minutes
          expiryPolicy: FetchPolicy.NETWORK_ONLY,
        }),
        notifyOnNetworkStatusChange: true,
        variables,
      });

      this.setState({
        availabilitySearch: {
          error: response.error,
          loading: response.loading,
          networkStatus: response.networkStatus,
          data: response?.data ?? defaultData,
          calendarFirstCellDate: variables.start,
        },
        availabilitySearchLoading: response.loading,
      }, () => {
        // TEMP Performance Hooks, TODO can be safely removed at any time
        addTiming(types.AVAILABILITY, 'Load Complete', true);
      });
      this.setState({
        availabilitySearchLoading: false,
      });
    } catch (e) {
      // For AbortErrors (from customFetch.js), we do not want standard error handling
      // as it means another request is already in progress and will return. If we get
      // a genuine error then we run the normal handling.
      if (e.message !== ABORT_ERROR_MESSAGE) {
        this.handleError('availabilitySearch')(e);
        this.setState({
          availabilitySearchLoading: false,
        });
      }
    }
  };

  /**
 * Handles date-specific searches which returns availability.
 * This is used throughout the standard journey and on initial
 * loads of amendments, before any changes are made
 * @returns {_Types.AvailabilitySearchResponse}
 */
  fetchAvailabilityTotal = async () => {
    const {
      client,
      variables: {
        partyMembers,
        payload: {
          end,
          start,
        },
        siteCode,
        productCode,
        placeType,
      },
    } = this.props;

    // TEMP Performance Hooks, TODO can be safely removed at any time
    addTiming(types.CALENDAR, 'Render Complete', true);

    const datesAreValid = this.validateDates();

    if (!datesAreValid) return null;

    const { variables } = getAvailabilityOptions({
      variables: {
        costPerNight: false,
        partyMembers,
        productCode,
        siteCode,
        start,
        end,
        placeType,
      },
    });

    try {
      const response = await client.query({
        query: GET_AVAILABILITY,
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        variables,
      });

      const { data: { availability: { offers } } } = response;
      await this.props.onCompleted({ offers });
      return response;
    } catch (e) {
      this.handleError('availabilityTotal')(e);
      return null;
    }
  };

  /**
 * Handles date-specific searches which returns availability,
 * pricing, restrictions, etc. for the currently selected
 * dates. This has special logic for dealing with amends
 * which can sometimes use a special amendment availability
 * endpoint. The cases for this are explained below.
 * @returns {_Types.AvailabilitySearchResponse}
 */
  fetchAmendmentAvailabilityTotal = async () => {
    const {
      client,
      variables: {
        partyMembers,
        payload: {
          end,
          start,
        },
        pitchId,
        siteCode,
      },
    } = this.props;
    const response = await client.query({
      query: GET_BOOKING_AMENDMENT_AVAILABILITY,
      variables: {
        siteCode,
        start: start.substring(0, 10),
        end: end.substring(0, 10),
        pitchId,
        partyMembers,
        pathBuilder: (path) => buildAmendmentAvailabilityPath(path),
      },
      fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });
    return response;
  }

  clearAvailabilityTotal = () => {
    this.setState({
      availabilityTotal: defaultData,
    });
  }

  /**
   * Handles date-specific searches which returns availability,
   * pricing, restrictions, etc. for the currently selected
   * dates. This has special logic for dealing with amends
   * which can sometimes use a special amendment availability
   * endpoint. The cases for this are explained below.
   * @returns {void}
   */
  fetchQuotePrice = async () => {
    const {
      variables,
      router,
      isAmend,
      booking,
    } = this.props;

    this.setState({
      availabilityTotal: {
        ...this.state.availabilityTotal,
        loading: true,
      },
    });

    /*
      Handling for deciding what requests we make for non-nightly pricing.

      Via amends we only call the GET_BOOKING_AMENDMENT_AVAILABILITY endpoint
      if we have changed any key booking features. We get the initial nightly
      pricing from the GET_CURRENT_USER_BOOKING_DETAILS call where it's
      returned on the pitch
    */
    let response;
    try {
      if (isAmend) {
        // Don't run the API request if there's no nights to get prices for
        const datesAreValid = this.validateDates();
        if (!datesAreValid) {
          response = this.clearAvailabilityTotal();
          return;
        }
        // If the user has updated their quote from their booking
        // we call a different API endpoint specifically for amends
        // pricing
        const hasUpdatedComponents = hasUpdatedQuoteFromBooking(
          router,
          variables,
          booking,
        );
        if (!hasUpdatedComponents) {
          /**
           * On the initial load into the amend, we need to call the
           * AvailabilityTotal (costPerNight=false) endpoint to get back
           * availability for the selected dates, particularly so the UI
           * in the pitch cards has all the data it needs
           */
          response = await this.fetchAvailabilityTotal();
        } else {
          /**
           * Then if the user updates anything critical on their booking
           * we call the amendment availability total endpoint which
           * makes a different API call to res for availability
           */
          response = await this.fetchAmendmentAvailabilityTotal();
        }
      } else {
        response = await this.fetchAvailabilityTotal();
      }
    } catch (error) {
      this.setState({
        availabilityTotal: {
          loading: false,
        },
      });
    }

    this.setState({
      availabilityTotal: {
        ...response,
        data: response?.data ?? defaultData,
      },
    });
  }

  validateDates = () => {
    const {
      variables: {
        payload: {
          end,
          start,
        },
      },
    } = this.props;

    if (!start && !end) {
      return false;
    }

    if (start === end || !end) {
      const inputDateError = new Error();
      inputDateError.message = dictionaryItem('AvailabilityCalendar', 'DepartureDate', 'Missing', 'Error');
      this.setState({ inputDateError });
      return false;
    }

    this.setState({ inputDateError: null });

    return true;
  }

  getAmendingSiteFromBooking = () => {
    const { booking, isAmend, router } = this.props;
    if (!isAmend || !booking) return null;
    const { componentId } = router.query ?? {};
    if (!booking || !booking?.campsiteBookings) return null;
    const amendingSite = booking.campsiteBookings.find(
      (campsiteBooking) => campsiteBooking.id === componentId,
    );
    return amendingSite ?? null;
  }

  render() {
    const {
      booking, children, variables, router, isAmend,
    } = this.props;
    const {
      availabilitySearch,
      availabilitySearchLoading,
      availabilityTotal,
      inputDateError,
    } = this.state;

    // Provides the sub-tree with the site we are currently amending
    // (if any). Saves elsewhere having to loop through the booking
    const amendingSite = this.getAmendingSiteFromBooking();

    // If there's no error and data then assume it's loading
    const loading = availabilitySearch.loading
      || (!availabilitySearch.error && !availabilitySearch.data);

    const hasUpdatedComponents = isAmend && hasUpdatedQuoteFromBooking(
      router,
      variables,
      booking,
    );

    return (
      <AvailabilityContext.Provider
        value={{
          amendingSite,
          availabilityTotal,
          availabilitySearch,
          booking,
          searchVariables: variables,
          loading: (availabilitySearchLoading ?? false) || (availabilityTotal?.loading ?? false),
          hasUpdatedComponents,
        }}
      >
        {children({
          inputDateError,
          availabilitySearch,
          availabilitySearchLoading,
          availabilityTotal,
          loading,
          fetchAvailabilitySearch: this.fetchAvailabilitySearch,
          fetchAvailabilityTotal: this.fetchQuotePrice,
        })}
      </AvailabilityContext.Provider>
    );
  }
}

export default compose(
  withApollo,
  withRouter,
  graphql(GET_CONFIGURATION, {
    props: ({ data }) => ({
      configuration: data.configuration,
      loading: data.loading,
    }),
  }),
  (WrappedComponent) => (props) => (
    // bypass withApollo and use custom ref, this is used to allow parent
    // QuoteCampsite to call fetchAvailabilityTotal externally.
    // eslint-disable-next-line react/prop-types
    <WrappedComponent {...props} ref={props.availabilitySearchQueryRef} />
  ),
)(AvailabilitySearchQuery);
